import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  ArrowUp,
  ArrowRight,
  Check,
  ChevronDown,
  Sparkles,
  GraduationCap,
  Flame,
  Target,
  Trophy,
  Clock3,
  Phone,
  Mail,
  MapPin,
  X,
  PlayCircle,
  Star,
  BookOpen,
  Brain,
  Users,
  MessageCircle,
  Lock,
  Bell,
  ShieldCheck,
  ExternalLink,
  Quote,
  Menu,
  Bookmark,
  FileText,
  DollarSign,
} from "lucide-react";
import { Link } from "react-router-dom";
import BatchCover, { batchHasCoverVideo } from "./components/BatchCover";
import "./App.css";
import BannerCarousel from "./components/BannerCarousel";
import StudentAccessModal from "./components/StudentAccessModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/* ---------------- Smooth Scroll Helper ---------------- */
const smoothScrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const headerOffset = 80;
  const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
  window.scrollTo({ top: y, behavior: "smooth" });
};

/* ---------------- Cursor Follower ---------------- */
const CursorFollower = () => {
  const dot = useRef(null);
  const ring = useRef(null);
  useEffect(() => {
    let rx = 0, ry = 0, x = 0, y = 0;
    const move = (e) => {
      x = e.clientX;
      y = e.clientY;
      if (dot.current) {
        dot.current.style.left = `${x}px`;
        dot.current.style.top = `${y}px`;
      }
    };
    const loop = () => {
      rx += (x - rx) * 0.12;
      ry += (y - ry) * 0.12;
      if (ring.current) {
        ring.current.style.left = `${rx}px`;
        ring.current.style.top = `${ry}px`;
      }
      requestAnimationFrame(loop);
    };
    window.addEventListener("mousemove", move);
    const raf = requestAnimationFrame(loop);
    const hoverables = document.querySelectorAll(
      "a, button, [role='button'], .cursor-grow"
    );
    const enter = () => {
      if (ring.current) {
        ring.current.style.width = "64px";
        ring.current.style.height = "64px";
        ring.current.style.borderColor = "rgba(0,229,255,0.9)";
      }
    };
    const leave = () => {
      if (ring.current) {
        ring.current.style.width = "40px";
        ring.current.style.height = "40px";
        ring.current.style.borderColor = "rgba(0,229,255,0.45)";
      }
    };
    hoverables.forEach((el) => {
      el.addEventListener("mouseenter", enter);
      el.addEventListener("mouseleave", leave);
    });
    return () => {
      window.removeEventListener("mousemove", move);
      cancelAnimationFrame(raf);
      hoverables.forEach((el) => {
        el.removeEventListener("mouseenter", enter);
        el.removeEventListener("mouseleave", leave);
      });
    };
  }, []);
  return (
    <>
      <div ref={dot} className="cursor-dot" />
      <div ref={ring} className="cursor-ring" />
    </>
  );
};

/* ---------------- Global texture overlay ---------------- */
const CinematicTexture = () => (
  <>
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[1] cinematic-dots opacity-[0.08]"
    />
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[1] opacity-30"
      style={{
        background:
          "radial-gradient(ellipse at 20% 10%, rgba(3,69,116,0.45), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(9,60,96,0.55), transparent 55%)",
      }}
    />
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[1] grain"
      style={{ opacity: 0.25 }}
    />
  </>
);

/* ---------------- Logo (real brand asset) ---------------- */
const Logo = ({ size = "md" }) => {
  const h =
    size === "xl"
      ? "h-32 md:h-48"
      : size === "lg"
      ? "h-24 md:h-28"
      : "h-16 md:h-[72px]";
  return (
    <div className="relative flex items-center select-none group" data-testid="brand-logo">
      <div
        aria-hidden
        className="absolute inset-0 rounded-full bg-cyan-glow/15 blur-2xl opacity-60 animate-pulse-glow pointer-events-none"
      />
      <div className={`${h} w-auto relative overflow-visible flex items-center`}>
        <img
          src="/brand-logo.png"
          alt="LogicsWallah"
          className="h-[200%] w-auto -my-[50%] relative brand-logo-img"
          draggable={false}
        />
      </div>
    </div>
  );
};

/* ---------------- Nav ---------------- */
const NAV_ITEMS = [
  { id: "courses", label: "Batches" },
  { id: "pricing", label: "Pricing" },
  { id: "results", label: "MBBS Success Stories" },
  { id: "contact", label: "Contact Us" },
];

/* ─────────────────────────────────── MDCAT Batches top-ribbon ─────────────────────────────────── */
const BatchesRibbon = ({ onOpen }) => {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1], delay: 0.1 }}
      className="sticky top-0 z-[60] w-full"
      data-testid="batches-ribbon"
    >
      <div className="relative w-full bg-gradient-to-r from-[#001b36] via-[#003a5c] to-[#001b36] border-b border-[rgba(0,229,255,0.28)] shadow-[0_8px_32px_rgba(0,10,25,0.5)]">
        <div aria-hidden className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(0,229,255,0.14),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={onOpen}
            className="group flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1 text-left"
            data-testid="batches-ribbon-btn"
            aria-label="Open MDCAT Batches"
          >
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-glow opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-glow" />
            </span>
            <span className="inline-flex items-center gap-2 text-cyan-glow text-[11px] sm:text-xs font-mono uppercase tracking-[0.18em] shrink-0">
              Live
            </span>
            <span className="hidden sm:inline text-white/40">·</span>
            <span className="text-white text-sm sm:text-base md:text-lg font-medium truncate">
              <span className="hidden sm:inline">Explore our </span>
              MDCAT Batches
              <span className="hidden md:inline text-white/55 font-normal"> — Freshers, Repeaters & more</span>
            </span>
            <span className="ml-auto sm:ml-3 inline-flex items-center gap-1 text-cyan-glow text-[11px] sm:text-xs font-mono uppercase tracking-[0.18em] group-hover:translate-x-0.5 transition-transform shrink-0">
              <span className="hidden sm:inline">View all</span>
              <span className="sm:hidden">View</span>
              <ArrowRight size={11} />
            </span>
          </button>
          <button
            onClick={() => setHidden(true)}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition"
            aria-label="Dismiss"
            data-testid="batches-ribbon-dismiss"
          >
            <X size={13} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};


const Nav = ({ onEnroll, onPickBatch }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    // lock body scroll when mobile menu open
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);
  const go = (id) => {
    setMobileOpen(false);
    // small delay so drawer can close before scroll
    setTimeout(() => smoothScrollTo(id), 200);
  };
  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
        className={`fixed top-[44px] sm:top-[48px] inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-2xl bg-ink-850/70 border-b border-white/10" : ""
        }`}
        data-testid="top-nav"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-6 py-4 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8 text-sm font-body text-white/80">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                className="relative group hover:text-white transition"
                data-testid={`nav-${item.id}`}
              >
                <span>{item.label}</span>
                <span className="absolute -bottom-1 left-0 right-0 h-[1.5px] bg-cyan-glow scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-out" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href="/login"
              className="hidden md:inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-cyan-glow transition font-body"
              data-testid="nav-student-portal"
            >
              Student Portal <ArrowRight size={14} />
            </a>
            <button
              className="hidden sm:inline-flex btn-primary text-sm"
              onClick={onEnroll}
              data-testid="nav-enroll-btn"
            >
              Login / Register <ArrowUpRight size={16} />
            </button>
            <button
              className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur-xl text-white/90 hover:border-cyan-glow/50 hover:text-cyan-glow transition"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[60] bg-ink-900/70 backdrop-blur-md lg:hidden"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 220 }}
              className="fixed top-0 right-0 bottom-0 z-[70] w-[85%] max-w-sm bg-ink-900/95 backdrop-blur-2xl border-l border-white/10 lg:hidden flex flex-col"
              data-testid="mobile-menu-drawer"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
                <span className="mono-label text-white/50">Menu</span>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:border-cyan-glow/40 hover:text-cyan-glow text-white/80 transition"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-8">
                <ul className="space-y-1">
                  {NAV_ITEMS.map((item, i) => (
                    <motion.li
                      key={item.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.08 + i * 0.07, duration: 0.4 }}
                    >
                      <button
                        onClick={() => go(item.id)}
                        className="group w-full flex items-center justify-between py-5 border-b border-white/5 text-left"
                        data-testid={`mobile-nav-${item.id}`}
                      >
                        <span className="display-serif text-white text-2xl group-hover:text-cyan-glow transition-colors duration-300">
                          {item.label}
                        </span>
                        <ArrowUpRight size={20} className="text-white/30 group-hover:text-cyan-glow group-hover:rotate-45 transition-all duration-400" />
                      </button>
                    </motion.li>
                  ))}
                </ul>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.5 }}
                  className="mt-10 space-y-3"
                >
                  <button
                    className="w-full btn-primary justify-center text-base"
                    onClick={() => { setMobileOpen(false); onPickBatch(); }}
                    data-testid="mobile-batches-btn"
                  >
                    MDCAT Batches <ArrowUpRight size={18} />
                  </button>
                  <a
                    href="/login"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/15 text-white/85 hover:border-cyan-glow/50 hover:text-cyan-glow text-base font-body transition"
                    data-testid="mobile-nav-student-portal"
                  >
                    Student Portal <ArrowRight size={16} />
                  </a>
                </motion.div>
                <div className="mt-10 pt-8 border-t border-white/5">
                  <div className="mono-label text-white/40 mb-3">— Talk to us</div>
                  <a href="tel:+923148799128" className="flex items-center gap-3 py-2 text-white/80 hover:text-cyan-glow transition">
                    <Phone size={14} className="text-cyan-glow" />
                    <span className="font-body text-sm">+92 314 8799128</span>
                  </a>
                  <a href="mailto:teamlogicians@gmail.com" className="flex items-center gap-3 py-2 text-white/80 hover:text-cyan-glow transition">
                    <Mail size={14} className="text-cyan-glow" />
                    <span className="font-body text-sm">teamlogicians@gmail.com</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

/* ---------------- Batch Selector Modal ---------------- */
const BatchSelectorModal = ({ open, onClose, courses, onPick }) => {
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-ink-900/85 backdrop-blur-md flex items-start md:items-center justify-center px-3 py-4 md:p-6 overflow-y-auto"
        onClick={onClose}
        data-testid="batch-selector-modal"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 260 }}
          className="relative w-full max-w-xl rounded-3xl border border-white/10 bg-gradient-to-br from-ink-800/95 to-ink-900/95 backdrop-blur-2xl p-5 md:p-7 overflow-hidden my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div aria-hidden className="absolute top-0 right-0 w-56 h-56 cinematic-dots-dense opacity-15 pointer-events-none" />
          <div className="relative flex items-center justify-between mb-5">
            <div>
              <div className="mono-label text-cyan-glow mb-1 text-[10px] md:text-xs">— Step 1 of 2</div>
              <h3 className="display-serif text-white text-xl md:text-2xl leading-tight">
                Which batch are you <span className="display-italic text-cyan-glow">interested</span> in?
              </h3>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center border border-white/10 hover:border-cyan-glow/40 hover:text-cyan-glow text-white/70 transition"
              aria-label="Close"
              data-testid="batch-selector-close"
            >
              <X size={15} />
            </button>
          </div>

          <div className="space-y-2.5 relative" data-testid="batch-selector-list">
            {courses.length === 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-center text-white/60 text-sm">
                Loading batches…
              </div>
            )}
            {courses.map((course, i) => (
              <BatchSelectorRow
                key={course.id || course.slug || i}
                course={course}
                index={i}
                onPick={onPick}
              />
            ))}
          </div>

          <p className="mt-4 text-[11px] text-white/45 font-body leading-relaxed">
            Tap an <span className="text-cyan-glow font-mono">OPEN</span> batch to jump straight to its registration
            form — or use the Student Portal login at the top of this page if you're already a Logician.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Row inside the batch-selector modal — mirrors the AccessBatchRow visual style
// so the entire site speaks one consistent batch-card language.
const BatchSelectorRow = ({ course, index, onPick }) => {
  const status = course.status || "open";
  const isOpen = status === "open";
  const tone =
    status === "open" ? "cyan" : status === "upcoming" ? "gold" : "rose";
  const toneCls =
    tone === "cyan"
      ? "border-cyan-glow/30 hover:border-cyan-glow/70 hover:bg-cyan-glow/[0.05]"
      : tone === "rose"
      ? "border-rose-300/20 hover:border-rose-300/40"
      : "border-[#ffcb47]/25 hover:border-[#ffcb47]/60";
  const badgeCls =
    tone === "cyan"
      ? "border-cyan-glow/40 text-cyan-glow bg-cyan-glow/5"
      : tone === "rose"
      ? "border-rose-300/40 text-rose-300 bg-rose-500/5"
      : "border-[#ffcb47]/40 text-[#ffcb47] bg-[#ffcb47]/5 animate-pulse-glow";
  return (
    <motion.div
      role="button"
      tabIndex={0}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 + index * 0.06, duration: 0.4 }}
      onClick={() => onPick({ key: status, slug: course.slug, registration_slug: course.registration_slug })}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPick({ key: status, slug: course.slug, registration_slug: course.registration_slug });
        }
      }}
      className={`group w-full text-left rounded-2xl border bg-ink-900/50 transition-all duration-300 overflow-hidden flex flex-col sm:flex-row cursor-pointer ${toneCls} ${!isOpen ? "opacity-80" : ""}`}
      data-testid={`batch-option-${status}`}
    >
      {/* Thumbnail — on mobile this is a full-width 16:9 hero so the cover
          poster reads cleanly; on tablet+ it becomes a wider side-thumb
          (sm:w-44 md:w-56) so the batch trailer poster is actually visible
          rather than a 112px sliver. We render `interactive={false}` here
          (poster + glowing play badge) so the ENTIRE ROW is a single click
          target — tapping the cover, body, or arrow all jump to the
          registration form. Students can preview the trailer on the
          dedicated batch hero card lower on the page (where iframe mounts
          are appropriate). This eliminates the prior bug where mobile users
          tapped the cover and nothing happened (iframe was swallowing the
          click via stopPropagation) and desktop showed a 112×63px iframe
          that looked broken. */}
      <div
        className={`relative w-full sm:w-44 md:w-56 ${batchHasCoverVideo(course) || course.cover_image ? "aspect-video" : "aspect-[1600/540] sm:aspect-auto"} shrink-0 overflow-hidden`}
        style={{
          background:
            tone === "cyan"
              ? "linear-gradient(135deg, rgba(0,229,255,0.3), rgba(12,24,48,0.95))"
              : tone === "rose"
              ? "linear-gradient(135deg, rgba(244,114,182,0.22), rgba(12,24,48,0.95))"
              : "linear-gradient(135deg, rgba(255,203,71,0.25), rgba(12,24,48,0.95))",
        }}
      >
        <BatchCover batch={course} alt={course.title} interactive={false} showWatchChip={true} />
        <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-black/50 sm:from-black/40 to-transparent pointer-events-none" />
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0 py-3 px-3 md:px-4 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className={`mono-label text-[9px] md:text-[10px] px-2 py-0.5 rounded-full border ${badgeCls}`}>
              {status === "open" ? "OPEN" : status === "upcoming" ? "REVEAL SOON" : "CLOSED"}
            </span>
            {course.status === "open" && course.seats_left ? (
              <span className="mono-label text-white/45 text-[9px] hidden sm:inline">{course.seats_left} seats</span>
            ) : null}
          </div>
          <div className="font-body text-white text-[14px] sm:text-sm md:text-base font-medium leading-tight line-clamp-2 sm:truncate">
            {course.title}
          </div>
          <div className="mono-label text-white/45 text-[9px] md:text-[10px] truncate sm:block mt-0.5">
            {course.audience || course.tagline || ""}
          </div>
        </div>
        <ArrowRight size={15} className="text-white/40 group-hover:text-cyan-glow group-hover:translate-x-1 transition-all shrink-0" />
      </div>
    </motion.div>
  );
};

/* ---------------- Hero ---------------- */
const Hero = ({ onPickBatch }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-[80vh] md:min-h-[100vh] pt-4 md:pt-16 pb-8 md:pb-20 overflow-hidden"
      data-testid="hero-section"
    >
      <div
        aria-hidden
        className="absolute inset-0 cinematic-dots-dense opacity-[0.12]"
        style={{
          maskImage: "radial-gradient(ellipse at 60% 40%, black 0%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at 60% 40%, black 0%, transparent 70%)",
        }}
      />
      {/* Concentric horizon rings */}
      <div aria-hidden className="absolute right-[5%] top-[45%] -translate-y-1/2 pointer-events-none hidden md:block">
        {[500, 800, 1100].map((s) => (
          <div
            key={s}
            className="absolute rounded-full border border-white/[0.06]"
            style={{ width: s, height: s, left: -s / 2, top: -s / 2 }}
          />
        ))}
      </div>
      <div
        aria-hidden
        className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 w-[1100px] h-[600px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,229,255,0.22), transparent 60%)",
          filter: "blur(50px)",
        }}
      />

      <motion.div style={{ y, opacity }} className="relative max-w-7xl mx-auto px-6 z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-4 md:mb-10"
        >
          <button
            type="button"
            onClick={() => { window.location.href = "/apply"; }}
            className="group inline-flex items-center gap-2 md:gap-3 max-w-full"
            data-testid="hero-batches-pointer"
            aria-label="View active MDCAT batches in the banner above"
          >
            {/* Bouncing up-arrow coin — physically nudges the eye toward the ribbon */}
            <motion.span
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: [0.45, 0, 0.55, 1] }}
              className="relative inline-flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-full border border-cyan-glow/40 bg-cyan-glow/10 text-cyan-glow shrink-0"
              aria-hidden
            >
              <ArrowUp size={12} strokeWidth={2.5} />
              <span className="absolute inset-0 rounded-full bg-cyan-glow/15 blur-[6px] -z-10" />
            </motion.span>
            {/* Live pulse */}
            <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-glow opacity-70" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-glow" />
            </span>
            <span className="mono-label text-[10px] sm:text-xs whitespace-nowrap text-cyan-glow/90 group-hover:text-cyan-glow transition-colors">
              <span className="sm:hidden">MDCAT '27 batches above</span>
              <span className="hidden sm:inline">Active MDCAT '27 batches — live above</span>
            </span>
            <span className="h-[1px] w-6 md:w-10 bg-cyan-glow/35 hidden xs:block" aria-hidden />
            <span className="mono-label text-white/45 text-[10px] sm:text-xs whitespace-nowrap hidden sm:inline group-hover:text-white/70 transition-colors">
              tap to view
            </span>
          </button>
        </motion.div>

        <div className="max-w-5xl">
          {/* Uniform line-height at each breakpoint keeps vertical rhythm balanced
              even with the nastaliq ک character (mobile 1.08 > 1 to give ک breathing room top+bottom). */}
          <h1 className="display-serif tracking-tight leading-[1.08] sm:leading-[0.95] md:leading-[0.92] text-white text-[1.75rem] xs:text-[2rem] sm:text-[3rem] md:text-[4.5rem] lg:text-[6rem] xl:text-[7rem]">
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="block"
            >
              Pa<span className="nastaliq">ک</span>istan's most
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55 }}
              className="block"
            >
              <span className="display-italic text-cyan-glow text-glow">premium</span>
              <span className="text-white/80"> &amp; </span>
              <span className="display-italic text-cyan-glow text-glow">affordable</span>
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="block"
            >
              MDCAT platform.
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-4 md:mt-10 max-w-2xl text-white/65 font-body text-sm md:text-xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            Pakistan's fastest-growing online MDCAT academy. We don't repeat lectures —
            we engineer the mind of a top-scorer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-5 md:mt-10 flex flex-wrap items-center gap-3 md:gap-4"
          >
            <button className="btn-primary text-sm md:text-base" onClick={onPickBatch} data-testid="hero-enroll-btn">
              Enroll in MDCAT Batch <ArrowRight size={16} />
            </button>
            <button
              className="btn-ghost text-sm md:text-base"
              onClick={() => document.getElementById("courses")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-explore-btn"
            >
              <PlayCircle size={16} /> Watch the film
            </button>
          </motion.div>
        </div>

        {/* Pakistan map — shown right after the Watch-the-film CTA,
            visible inside the hero's first viewport area. */}
        <PakistanMapStory />

        {/* LW Features strip — compact, cinematic "Inside LogicsWallah" pill row.
            Replaces the older taller 8-card grid. Mobile: 2-col micro-grid.
            Tablet: 4-col. Desktop: single continuous 8-item row.
            World-class creative pattern: minimal iconography + kinetic hover glow. */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-8 md:mt-12"
          data-testid="hero-features-grid"
        >
          <div className="flex items-center gap-3 mb-3 md:mb-4">
            <span className="h-[1px] w-8 md:w-10 bg-cyan-glow/70" />
            <span className="mono-label text-[10px] sm:text-xs">— Inside LogicsWallah</span>
            <span className="mono-label text-[9px] text-white/35 hidden sm:inline ml-auto">8 pillars · one LMS</span>
          </div>
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.015] backdrop-blur-xl overflow-hidden">
            {/* ambient cinematic dots + beam */}
            <div aria-hidden className="absolute inset-0 cinematic-dots opacity-[0.05] pointer-events-none" />
            <div aria-hidden className="absolute -top-10 left-1/4 w-40 h-40 rounded-full bg-cyan-glow/10 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 divide-x divide-white/5">
              {[
                { Icon: PlayCircle, t: "Lectures", d: "Concept-first" },
                { Icon: Sparkles, t: "MCQs", d: "7,000+ Qs" },
                { Icon: MessageCircle, t: "Doubts", d: "Daily · live" },
                { Icon: Quote, t: "Mentors", d: "1:1 MBBS" },
                { Icon: Bookmark, t: "Revision", d: "Spaced deck" },
                { Icon: Trophy, t: "Ranks", d: "Live boards" },
                { Icon: FileText, t: "Notes", d: "Printable" },
                { Icon: Users, t: "Community", d: "Private feed" },
              ].map((f, i) => (
                <motion.div
                  key={f.t}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 1.5 + i * 0.04 }}
                  className="relative group p-3 md:p-4 first:border-l-0 border-b border-b-white/5 sm:border-b-0 hover:bg-cyan-glow/[0.04] transition-all duration-300"
                  data-testid={`hero-feature-${i}`}
                >
                  <div aria-hidden className="absolute top-1/2 -translate-y-1/2 left-3 w-6 h-6 rounded-full bg-cyan-glow/0 group-hover:bg-cyan-glow/20 blur-md transition-all duration-300" />
                  <div className="relative flex items-center gap-2.5">
                    <div className="shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-lg border border-cyan-glow/20 bg-cyan-glow/[0.06] flex items-center justify-center group-hover:border-cyan-glow/60 group-hover:bg-cyan-glow/[0.12] transition-all">
                      <f.Icon size={13} className="text-cyan-glow" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-white text-[12px] md:text-[13px] font-semibold leading-tight truncate">{f.t}</div>
                      <div className="mono-label text-white/40 text-[9px] leading-tight truncate">{f.d}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Compact stats strip — moved below features. Still here, just smaller & quieter. */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.9 }}
          className="mt-6 md:mt-10 grid grid-cols-4 gap-px bg-white/5 rounded-xl overflow-hidden border border-white/10"
          data-testid="hero-stat-strip"
        >
          {[
            { n: "30K+", l: "Aspirants" },
            { n: "100K+", l: "Community · 1 yr" },
            { n: "177", l: "Highest MDCAT · PK" },
            { n: "4.96", l: "Avg. rating" },
          ].map((s, i) => (
            <div key={i} className="p-3 md:p-4 bg-ink-850/60 text-center">
              <div className="number-big text-lg md:text-2xl text-white leading-none">{s.n}</div>
              <div className="mt-1 mono-label text-white/45 text-[8px] md:text-[10px] leading-tight truncate">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9 }}
        className="hidden md:flex absolute bottom-4 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-white/40 text-xs"
      >
        <span className="mono-label">Scroll</span>
        <div className="w-[1px] h-10 bg-gradient-to-b from-cyan-glow to-transparent" />
      </motion.div>
    </section>
  );
};

/* ---------------- Ticker Marquee ---------------- */
const Ticker = () => {
  const items = [
    "MDCAT 2027",
    "Freshers · Repeaters · Parallel Prep",
    "Live Cinematic Classes",
    "Per-lecture · Per-chapter · Full-book Analytics",
    "Private Doctor Mentors",
    "Admissions Open",
    "HQ · Gujranwala · All Pakistan · Online",
  ];
  return (
    <div className="relative border-y border-white/10 bg-ink-900/60 overflow-hidden py-6">
      <div className="marquee-wrap">
        <div className="flex gap-16 animate-marquee whitespace-nowrap">
          {[...items, ...items, ...items].map((t, i) => (
            <div key={i} className="flex items-center gap-16 text-white/70">
              <span className="display-italic text-3xl md:text-4xl">{t}</span>
              <span className="w-2 h-2 rounded-full bg-cyan-glow/80" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Pakistan Map Story ---------------- */
const PakistanMapStory = () => {
  // Cities positioned as % of the real Pakistan SVG (1000x959 viewBox, Mercator)
  const cities = [
    { name: "Gilgit", x: 69, y: 8 },
    { name: "Peshawar", x: 62, y: 22 },
    { name: "Islamabad", x: 70, y: 24 },
    { name: "Gujranwala", x: 76, y: 30, hq: true },
    { name: "Lahore", x: 78, y: 34 },
    { name: "Faisalabad", x: 71, y: 36 },
    { name: "Multan", x: 63, y: 46 },
    { name: "Quetta", x: 37, y: 52 },
    { name: "Sukkur", x: 51, y: 63 },
    { name: "Karachi", x: 38, y: 86 },
    { name: "Gwadar", x: 14, y: 90 },
  ];

  return (
    <section className="relative -mx-6 py-4 md:py-10 overflow-hidden" data-testid="map-story-section">
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="relative overflow-hidden">
          {/* MAP CONTAINER — speech bubbles overlaid as if emerging from map points */}
          <div className="relative w-full aspect-[4/5] sm:aspect-[1/1] md:aspect-[16/11] lg:aspect-[16/10]">
            {/* Real Pakistan country silhouette (SVG, cyan-dotted fill) */}
            <img
              src="/pakistan-map.svg"
              alt="Pakistan"
              draggable={false}
              className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
              style={{
                filter:
                  "drop-shadow(0 0 40px rgba(0,229,255,0.25)) drop-shadow(0 0 12px rgba(0,229,255,0.4))",
              }}
              data-testid="pakistan-map-svg"
            />

            {/* Beam line from Karachi → Gujranwala (SVG overlay) */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 w-full h-full pointer-events-none"
              aria-hidden
            >
              <line
                x1="38" y1="86"
                x2="76" y2="30"
                stroke="rgba(0,229,255,0.6)"
                strokeWidth="0.25"
                className="beam"
                vectorEffect="non-scaling-stroke"
                style={{ strokeWidth: 1.4 }}
              />
            </svg>

            {/* City dots + labels */}
            {cities.map((c) => (
              <div key={c.name}>
                <div
                  className={`map-dot ${c.hq ? "hq" : ""}`}
                  style={{
                    left: `${c.x}%`,
                    top: `${c.y}%`,
                    background: c.hq ? "#ffcb47" : "#00e5ff",
                    boxShadow: c.hq
                      ? "0 0 0 4px rgba(255,203,71,0.2), 0 0 22px rgba(255,203,71,0.8)"
                      : "0 0 0 3px rgba(0,229,255,0.18), 0 0 16px rgba(0,229,255,0.7)",
                    width: c.hq ? 13 : 8,
                    height: c.hq ? 13 : 8,
                  }}
                  data-testid={`map-dot-${c.name.toLowerCase()}`}
                />
                <div
                  className="absolute mono-label pointer-events-none whitespace-nowrap"
                  style={{
                    left: `${c.x}%`,
                    top: `calc(${c.y}% + 14px)`,
                    transform: "translate(-50%, 0)",
                    color: c.hq ? "#ffcb47" : "rgba(255,255,255,0.55)",
                    fontSize: c.hq ? "10px" : "9px",
                    letterSpacing: "0.28em",
                  }}
                >
                  {c.name}
                  {c.hq && <span className="ml-1 text-white/70">· HQ</span>}
                </div>
              </div>
            ))}

            {/* STUDENT SPEECH BUBBLE — positioned ABOVE (near top-left near Islamabad/Peshawar region),
                feels like the question travelling DOWN toward HQ. Bumped opacity for readability. */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
              className="absolute z-20"
              style={{ left: "4%", top: "8%" }}
              data-testid="student-bubble"
            >
              {/* Pointer tail DOWN-RIGHT toward Karachi dot (which lives bottom-left of the map) */}
              <svg
                width="70" height="48"
                className="absolute left-full top-full hidden xs:block"
                style={{ overflow: "visible" }}
                aria-hidden
              >
                <line x1="0" y1="0" x2="60" y2="48" stroke="rgba(0,229,255,0.6)" strokeWidth="1.3" strokeDasharray="3 4" />
                <circle cx="60" cy="48" r="3" fill="#00e5ff" />
              </svg>
              <div className="relative rounded-xl rounded-tl-none bg-ink-700/95 border border-cyan-glow/50 backdrop-blur-xl px-3 py-2 md:px-4 md:py-3 max-w-[200px] sm:max-w-[240px] shadow-[0_10px_40px_rgba(0,229,255,0.22)]">
                <div className="flex items-center gap-2 mb-1 md:mb-1.5">
                  <div className="shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-cyan-glow/40 to-cyan-glow/10 border border-cyan-glow/50 flex items-center justify-center text-cyan-glow text-[11px] md:text-xs font-semibold">
                    {/* Neutral, diverse student avatar using initials — feels more authentic than stock image */}
                    A
                  </div>
                  <div className="mono-label text-cyan-glow flex items-center gap-1 md:gap-1.5 text-[8px] md:text-[10px]">
                    <MessageCircle size={9} /> Student · Karachi
                  </div>
                </div>
                <p className="font-body text-white text-[11px] sm:text-xs md:text-sm leading-snug">
                  "Hassaan Sir, what's <span className="text-cyan-glow font-semibold">LW</span>?"
                </p>
              </div>
            </motion.div>

            {/* HASSAAN SIR'S REPLY BUBBLE — now positioned BELOW (bottom-right), so the conversation
                reads top→bottom: student asks, Hassaan replies. */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.8, delay: 0.95, ease: [0.2, 0.8, 0.2, 1] }}
              className="absolute z-20"
              style={{ right: "6%", bottom: "10%", maxWidth: "230px" }}
              data-testid="hassaan-bubble"
            >
              {/* Pointer tail UP-LEFT toward Gujranwala HQ dot */}
              <svg
                className="absolute right-[32px] bottom-full hidden xs:block"
                width="70" height="60"
                style={{ overflow: "visible" }}
                aria-hidden
              >
                <line x1="40" y1="60" x2="-18" y2="8" stroke="rgba(255,203,71,0.6)" strokeWidth="1.3" strokeDasharray="4 5" />
                <circle cx="-18" cy="8" r="3.5" fill="#ffcb47" />
              </svg>
              <div className="relative rounded-xl rounded-br-none bg-gradient-to-br from-[#1a1208]/97 to-ink-850/97 border border-[#ffcb47]/55 backdrop-blur-xl p-3 md:p-4 shadow-[0_14px_50px_rgba(255,203,71,0.24)]">
                <div className="flex items-center gap-2 md:gap-3">
                  <div
                    className="shrink-0 w-9 h-9 md:w-11 md:h-11 rounded-full overflow-hidden border-2 border-[#ffcb47] shadow-[0_0_14px_rgba(255,203,71,0.6)]"
                    data-testid="hassaan-photo"
                  >
                    <img src="/sir-hassaan.jpeg" alt="Hassaan Sir" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="mono-label flex items-center gap-1 md:gap-1.5 text-[9px] md:text-[10px]" style={{ color: "#ffcb47" }}>
                    <Quote size={9} /> Hassaan Sir
                  </div>
                </div>
                <p className="display-italic text-white text-[11px] sm:text-xs md:text-sm leading-snug mt-2 md:mt-3">
                  "LW is where you follow <span style={{ color: "#ffcb47" }}>uniqueness</span> — and the numbers follow <span className="text-cyan-glow">you.</span>"
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Section Heading ---------------- */
const SectionHeading = ({ kicker, title, italic, subtitle, align = "left" }) => (
  <div className={`${align === "center" ? "text-center mx-auto" : ""} max-w-3xl`}>
    <div className={`flex items-center gap-3 mb-6 ${align === "center" ? "justify-center" : ""}`}>
      <span className="h-[1px] w-10 bg-cyan-glow/70" />
      <span className="mono-label">{kicker}</span>
    </div>
    <h2 className="display-serif tracking-tight text-5xl md:text-7xl leading-[0.95] text-white">
      {title}{" "}
      <span className="display-italic text-cyan-glow">{italic}</span>
    </h2>
    {subtitle && (
      <p className={`mt-6 text-white/60 font-body text-lg leading-relaxed max-w-2xl ${align === "center" ? "mx-auto" : ""}`}>
        {subtitle}
      </p>
    )}
  </div>
);

/* ---------------- Testing System Component (inside course card) ---------------- */
const TestingSystem = () => {
  const tiers = [
    { label: "Per lecture", sub: "quizzes" },
    { label: "Per chapter", sub: "quizzes" },
    { label: "Quarter book", sub: "quizzes" },
    { label: "Half book", sub: "quizzes" },
    { label: "Full book", sub: "quizzes" },
  ];
  return (
    <div className="mt-6 rounded-2xl border border-cyan-glow/20 bg-ink-900/50 p-5 md:p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-cyan-glow/10 border border-cyan-glow/30 flex items-center justify-center">
            <Brain size={15} className="text-cyan-glow" />
          </div>
          <div>
            <div className="mono-label text-white/60">LW Testing System</div>
            <div className="font-body text-white text-sm">Five-layer analytics, never miss a gap</div>
          </div>
        </div>
        <span className="mono-label text-cyan-glow hidden sm:inline">Analytics included</span>
      </div>
      <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
        {tiers.map((t, i) => (
          <div
            key={t.label}
            className="relative rounded-lg border border-white/10 bg-ink-800/40 p-2 sm:p-3 text-center overflow-hidden"
          >
            <div className="mono-label text-cyan-glow text-[8px] sm:text-[9px] mb-1">0{i + 1}</div>
            <div className="font-body text-white text-[10px] sm:text-[11px] md:text-xs leading-tight break-words">{t.label}</div>
            <div className="mono-label text-white/40 text-[8px] sm:text-[9px] mt-1 hidden sm:block truncate">{t.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Hidden Features Disclosure ---------------- */
const HiddenFeatures = () => {
  const [open, setOpen] = useState(false);
  const items = [
    "MDCAT guidance sessions",
    "Live leaderboards on LMS",
    "Daily Quran, Namaz & Tahajjud streaks",
    "LW Classrooms",
    "Ask-the-subject-head sessions",
    "Study streak system",
  ];
  return (
    <div className="mt-6 rounded-xl border border-white/10 bg-ink-900/40 overflow-hidden" data-testid="hidden-features">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-ink-800/40 transition"
      >
        <div className="flex items-center gap-3">
          <Lock size={14} className="text-cyan-glow" />
          <span className="font-body text-white/85 text-sm">
            …and a lot more we didn't list — so you don't get overwhelmed.
          </span>
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }}>
          <ChevronDown size={16} className="text-white/60" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <ul className="px-4 pb-5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-white/70 font-body text-sm">
              {items.map((i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-glow" />
                  {i}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ---------------- Courses ---------------- */

const FreshersRepeatersCard = ({ course, onEnroll, isBig }) => {
  const Icon = Flame;
  return (
    <div
      className={`group relative rounded-3xl overflow-hidden border border-cyan-glow/25 bg-gradient-to-br from-ink-800/70 to-ink-850/90 backdrop-blur-xl p-8 md:p-10 ${
        isBig ? "lg:col-span-7 lg:row-span-2 min-h-[620px]" : "lg:col-span-5 min-h-[260px]"
      } cursor-grow`}
      data-testid={`course-card-${course.slug}`}
    >
      <div aria-hidden className="absolute top-0 right-0 w-56 h-56 cinematic-dots-dense opacity-20 pointer-events-none" />
      <div className="relative flex flex-col h-full justify-between">
        <div>
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-cyan-glow/10 border border-cyan-glow/30 flex items-center justify-center">
                <Icon size={20} className="text-cyan-glow" />
              </div>
              <span className="mono-label text-white/60">{course.audience}</span>
            </div>
            {course.badge && (
              <span className="mono-label text-[10px] px-3 py-1 rounded-full border border-cyan-glow/40 text-cyan-glow">
                {course.badge}
              </span>
            )}
          </div>

          <h3 className="display-serif tracking-tight text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
            MDCAT Freshers
            <br />
            <span className="display-italic text-cyan-glow">&amp; Repeaters</span>
          </h3>
          <p className="mt-5 text-white/65 text-lg max-w-2xl leading-relaxed">
            {course.tagline}
          </p>

          <div className="mt-7 flex flex-wrap gap-2">
            {course.subjects.map((s) => (
              <span key={s} className="px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs text-white/70 font-body">
                {s}
              </span>
            ))}
          </div>

          {/* Feature 1 — Concept-first lectures with PROOF button */}
          <div className="mt-8 flex items-start gap-3">
            <Check size={16} className="mt-1 text-cyan-glow shrink-0" />
            <div className="flex-1">
              <div className="font-body text-white">
                Concept-first lectures by the best MDCAT instructors
              </div>
              <a
                href="https://logicswallah.pk/demos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-full border border-[#ffcb47]/40 bg-[#ffcb47]/10 text-[#ffcb47] text-xs mono-label hover:bg-[#ffcb47]/20 hover:border-[#ffcb47]/80 transition-all"
                data-testid="proof-btn"
              >
                <ShieldCheck size={12} /> See the proof
                <ExternalLink size={11} />
              </a>
            </div>
          </div>

          {/* Testing system showcase */}
          <TestingSystem />

          {/* More feature items */}
          <ul className="mt-6 space-y-3">
            {[
              { icon: MessageCircle, text: "Daily doubt-solving sessions with expert teachers" },
              { icon: BookOpen, text: "MDCAT e-notes & lecture notes — for quick revision" },
              { icon: Users, text: "Private doctor-mentor circle — 1:1 MBBS mentorship" },
              { icon: Bell, text: "Private daily accountability — guaranteed fewer backlogs" },
            ].map((f) => (
              <li key={f.text} className="flex items-start gap-3 text-white/80 font-body">
                <f.icon size={15} className="mt-1 text-cyan-glow shrink-0" />
                <span>{f.text}</span>
              </li>
            ))}
          </ul>

          {/* Hidden features disclosure */}
          <HiddenFeatures />
        </div>

        <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 sm:gap-6">
          <div>
            <div className="mono-label text-white/40">Duration</div>
            <div className="font-body text-white text-lg mt-1 flex items-center gap-2">
              <Clock3 size={16} className="text-cyan-glow shrink-0" />
              {course.duration}
            </div>
            <div className="mt-3 mono-label text-white/40">
              <span className="text-cyan-glow">Limited bookings only</span>
            </div>
          </div>
          <div className="self-start sm:self-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0 w-full sm:w-auto">
            {/* "See pricing" — scrolls to the in-sync pricing section below instead of
                duplicating the fee here (which used to drift from admin-set prices). */}
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/15 bg-white/[0.04] text-white font-body font-medium text-sm hover:border-cyan-glow/60 hover:bg-white/[0.06] transition-all whitespace-nowrap"
              data-testid={`course-see-pricing-${course.slug}`}
            >
              <DollarSign size={14} className="text-cyan-glow" /> See pricing
            </a>
            {course.registration_slug || course.slug ? (
              <Link
                to={`/register/${course.registration_slug || course.slug}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-cyan-glow text-ink-850 font-body font-semibold text-sm hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all whitespace-nowrap"
                data-testid={`course-register-${course.slug}`}
              >
                Register <ArrowUpRight size={16} />
              </Link>
            ) : (
              <button
                onClick={() => onEnroll(course)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-cyan-glow text-ink-850 font-body font-semibold text-sm hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all whitespace-nowrap"
                data-testid={`course-enroll-${course.slug}`}
              >
                Reserve my seat <ArrowUpRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClosedBatchCard = ({ course, onRedirect }) => {
  return (
    <div
      className="group relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-ink-800/40 to-ink-850/70 backdrop-blur-xl p-8 md:p-10 min-h-[260px] lg:col-span-5 cursor-grow"
      data-testid={`course-card-${course.slug}`}
    >
      <div aria-hidden className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 cinematic-dots opacity-30" />
      </div>
      <div className="relative flex flex-col h-full justify-between">
        <div>
          <div className="flex items-center justify-between mb-5 gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center">
                <Target size={18} className="text-white/50" />
              </div>
              <span className="mono-label text-white/50">{course.audience}</span>
            </div>
            <span className="mono-label text-[10px] px-3 py-1 rounded-full border border-rose-300/40 text-rose-300 bg-rose-300/5">
              BATCH CLOSED
            </span>
          </div>
          <h3 className="display-serif tracking-tight text-white/85 text-3xl md:text-4xl leading-[1]">
            MDCAT Repeaters
            <br />
            <span className="display-italic text-white/50">— Comeback Program</span>
          </h3>
          <p className="mt-4 text-white/55 leading-relaxed max-w-md">
            April's Comeback Batch was our last exclusive Repeaters intake. The good news —{" "}
            <span className="text-cyan-glow">Repeaters are equally welcomed</span> inside the{" "}
            <strong className="text-white">Freshers &amp; Repeaters batch.</strong> Same features. Same fire.
          </p>
          <div className="mt-5 mono-label text-white/40">
            Previous duration was {course.duration}
          </div>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={onRedirect}
            className="btn-ghost text-sm"
            data-testid={`course-redirect-${course.slug}`}
          >
            Join Freshers &amp; Repeaters instead <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

const UpcomingBatchCard = ({ course }) => {
  return (
    <div
      className="group relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-ink-800/40 to-ink-850/70 backdrop-blur-xl p-8 md:p-10 min-h-[260px] lg:col-span-7 cursor-grow"
      data-testid={`course-card-${course.slug}`}
    >
      <div aria-hidden className="absolute top-0 right-0 w-72 h-72 cinematic-dots-dense opacity-10 pointer-events-none" />
      <div className="relative flex flex-col h-full justify-between">
        <div>
          <div className="flex items-center justify-between mb-5 gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center">
                <GraduationCap size={18} className="text-white/60" />
              </div>
              <span className="mono-label text-white/50">{course.audience}</span>
            </div>
            <span className="mono-label text-[10px] px-3 py-1 rounded-full border border-[#ffcb47]/40 text-[#ffcb47] bg-[#ffcb47]/5 animate-pulse-glow">
              REVEAL SOON
            </span>
          </div>
          <h3 className="display-serif tracking-tight text-white text-3xl md:text-5xl leading-[0.95]">
            MDCAT <span className="display-italic text-cyan-glow">Parallel</span> Prep
          </h3>
          <p className="mt-4 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            {course.tagline}
          </p>

          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "F.Sc Part 1" },
              { label: "F.Sc Part 2" },
              { label: "MDCAT prep woven in" },
              { label: "MDCAT 2027 · 2028" },
            ].map((t) => (
              <div key={t.label} className="px-3 py-3 rounded-lg border border-white/10 bg-white/[0.03]">
                <div className="mono-label text-cyan-glow text-[9px] mb-1">Track</div>
                <div className="font-body text-white/85 text-sm leading-tight">{t.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-6">
          <div>
            <div className="mono-label text-white/40">Launch window</div>
            <div className="font-body text-white text-base sm:text-lg mt-1 flex items-center gap-2 whitespace-nowrap">
              <Sparkles size={16} className="text-[#ffcb47] shrink-0" />
              Now open
            </div>
          </div>
          <span
            className="self-start sm:self-auto shrink-0 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full border border-white/15 text-white/50 mono-label text-[10px] sm:text-xs cursor-not-allowed whitespace-nowrap"
            data-testid="upcoming-badge"
          >
            Enrollment not yet open
          </span>
        </div>
      </div>
    </div>
  );
};

const Courses = ({ courses, onEnroll }) => {
  // We render in a specific order: open (big), closed, upcoming
  const open = courses.find((c) => c.status === "open");
  const closed = courses.find((c) => c.status === "closed");
  const upcoming = courses.find((c) => c.status === "upcoming");

  return (
    <section id="courses" className="relative py-32 md:py-40" data-testid="courses-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <SectionHeading
            kicker="01 — Courses & Batches"
            title="Three tracks."
            italic="One standard: elite."
            subtitle="Every LogicsWallah batch is engineered around the student's starting point — not a generic syllabus. Choose your track. We'll engineer the rest."
          />
          <div className="hidden md:flex items-center gap-2 mono-label text-white/50">
            <Sparkles size={14} className="text-cyan-glow" />
            2027 intake · limited seats
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {open && (
            <motion.div
              id={`batch-${open.slug}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7 lg:row-span-2 scroll-mt-24"
            >
              <FreshersRepeatersCard course={open} onEnroll={onEnroll} isBig />
            </motion.div>
          )}
          {closed && (
            <motion.div
              id={`batch-${closed.slug}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-5 scroll-mt-24"
            >
              <ClosedBatchCard course={closed} onRedirect={() => onEnroll({ course: open?.title, plan: "Pro", redirectFromClosed: true })} />
            </motion.div>
          )}
          {upcoming && (
            <motion.div
              id={`batch-${upcoming.slug}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="lg:col-span-5 scroll-mt-24"
            >
              <UpcomingBatchCard course={upcoming} />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Results (Muaz + Hassaan Sir + success stories) ---------------- */
const successStories = [
  {
    name: "Qandeel",
    track: "Repeaters Batch · Gap year",
    score: "RMU",
    descriptor: "Rank 4 MBBS University · Punjab",
    quote: (
      <>
        On 12 Nov 2024 she sent a voice note:
        <span className="display-italic text-white">
          {" "}"Mai nai apkai naye lectures liye hain, aur bilkul bhi bore nahi hui —"
        </span>{" "}
        — in a gap year where most students quit, she came back to finish.
      </>
    ),
  },
  {
    name: "Eman",
    track: "Parallel MDCAT Batch",
    score: "RMU",
    descriptor: "Survived brutal PMDC policies · Top 4 University · Punjab",
    image: "/eman.jpeg",
    imagePosition: "center 20%",
    quote: (
      <>
        On 6 Nov 2024 she wrote:
        <span className="display-italic text-white">
          {" "}"Sir I devoted myself completely to LW. Ab aapne aur aapki team ne next year In-sha-Allah mughe KEMU tak le kar jana hai."
        </span>{" "}
        She became a fresher at RMU.
      </>
    ),
  },
  {
    name: "Fatima Faisal",
    track: "Fresher",
    score: "KEMU",
    descriptor: "Rank 26 across Punjab",
    quote: (
      <>
        Months before MDCAT she told Hassaan Sir she'd tag him in her Insta story from inside KEMU.
        Today,
        <span className="display-italic text-white"> she's inside KEMU.</span>
      </>
    ),
  },
  {
    name: "Shakir Fareed",
    track: "Restart · Repeater",
    score: "KEMU",
    descriptor: "Rank 247",
    quote: (
      <>
        <span className="display-italic text-white">
          "I used to watch LW's conceptual reels when I was a fresher. When I had to restart, I realised my concepts were weak — that's when I joined LW for real."
        </span>
      </>
    ),
  },
  {
    name: "Abdus Samad Sarim · Farkhanda",
    track: "Khyber Pakhtunkhwa",
    score: "174 · 174",
    descriptor: "Rank 5 across KPK",
    quote: (
      <>
        From small KPK towns to being ranked 5th of the whole province — two logicians, same score, same standard.
      </>
    ),
  },
  {
    name: "Sajida · Lucky Bhatia",
    track: "Sindh",
    score: "MBBS",
    descriptor: "Entered their dream universities",
    image: "/sajida.jpeg",
    imagePosition: "center 30%",
    quote: (
      <>
        Even from our smallest Sindh cohort, two logicians made it to their dream medical universities — proof that geography never caps ambition.
      </>
    ),
  },
];

const Results = () => {
  return (
    <section id="results" className="relative py-32 md:py-40 border-t border-white/5" data-testid="results-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <SectionHeading
              kicker="02 — Results & Success Stories"
              title="Receipts."
              italic="Not promises."
            />
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <div className="number-big text-6xl md:text-7xl text-cyan-glow text-glow leading-none">
              17%+
            </div>
            <div className="mt-3 mono-label text-white/60 leading-snug">
              2025 students secured a Govt. MBBS seat.
              <br />
              50%+ lost it by a few aggregate points.
              <br />
              <span className="text-cyan-glow">In our 1st anniversary year — this is academic excellence.</span>
            </div>
          </div>
        </div>

        {/* MUAZ HERO CARD — with Hassaan Sir · Rank 4 Punjab */}
        <motion.article
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative rounded-3xl overflow-hidden border border-[#ffcb47]/30 mb-10"
          data-testid="muaz-hero"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Photo */}
            <div className="lg:col-span-7 relative min-h-[400px] lg:min-h-[560px]">
              <img
                src="/muaz-hassaan.jpeg"
                alt="Muaz Rehman with Hassaan Sir — All Punjab Rank 4"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-ink-850/30 to-ink-850" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-850 via-transparent to-transparent" />
              <div className="absolute inset-0 cinematic-dots opacity-10 mix-blend-screen" />
              {/* Top chip */}
              <div className="absolute top-6 left-6 flex items-center gap-2">
                <span className="mono-label text-[#ffcb47] bg-[#1a1208]/70 border border-[#ffcb47]/40 rounded-full px-3 py-1.5 backdrop-blur">
                  Alhamdulillah — Pakistan is getting logicians now.
                </span>
              </div>
            </div>
            {/* Text */}
            <div className="lg:col-span-5 p-8 md:p-12 bg-gradient-to-br from-ink-800/70 to-ink-850 flex flex-col justify-center">
              <div className="mono-label text-cyan-glow mb-4">— MDCAT 2025 · Toppers Feature</div>
              <h3 className="display-serif text-white text-4xl md:text-5xl leading-[1] tracking-tight">
                Muaz Rehman
              </h3>
              <div className="display-italic text-[#ffcb47] text-xl md:text-2xl mt-2">
                All Punjab Rank 4
              </div>
              <div className="mt-6 number-big text-7xl md:text-8xl text-white text-glow leading-none">
                175
              </div>
              <div className="mono-label text-white/50 mt-2">MDCAT SCORE · KEMU</div>
              <p className="mt-6 text-white/75 font-body leading-relaxed">
                On 6 December 2024, Muaz sent a voice note to Hassaan Sir:{" "}
                <span className="display-italic text-white">
                  "Sir jab bhi lectures le raha hota hoon, dil mein hota hai ke aapko yeh chez batana hai. Academies mein teachers ke lectures sun leta tha, book cover ho jaati thi, lekin andar kuch missing tha. Sir now I feel complete — ab I am in peace."
                </span>
              </p>
              <div className="mt-8 flex items-center gap-3">
                <Trophy size={18} className="text-[#ffcb47]" />
                <span className="mono-label text-white/60">Rank 4 · Whole of Punjab</span>
              </div>
            </div>
          </div>
        </motion.article>

        {/* Success stories grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {successStories.map((s, i) => {
            const hasImage = Boolean(s.image);
            return (
              <motion.article
                key={s.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`story-card group relative rounded-2xl overflow-hidden cursor-grow ${
                  hasImage
                    ? "border border-[#ffcb47]/25 min-h-[440px] md:min-h-[480px]"
                    : "border border-white/10 bg-gradient-to-br from-ink-800/60 to-ink-850/80 backdrop-blur-xl p-6 md:p-7"
                }`}
                data-testid={`story-card-${i}`}
              >
                {hasImage ? (
                  <>
                    {/* Cinematic background photo */}
                    <img
                      src={s.image}
                      alt={s.name}
                      draggable={false}
                      className="story-card-photo absolute inset-0 w-full h-full object-cover transition-transform duration-[2500ms] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.06]"
                      style={{ objectPosition: s.imagePosition || "center center" }}
                    />
                    {/* Bottom → top readable shade (deep ink) */}
                    <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/80 to-transparent" />
                    {/* Left cinematic slab so top chip reads on any frame */}
                    <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-ink-900/75 via-ink-900/20 to-transparent" />
                    {/* Tiny cyan glow bloom (matches other hero cards) */}
                    <div aria-hidden className="absolute -top-24 -right-24 w-64 h-64 rounded-full pointer-events-none"
                         style={{ background: "radial-gradient(circle, rgba(0,229,255,0.18), transparent 60%)", filter: "blur(30px)" }} />
                    <div aria-hidden className="absolute inset-0 cinematic-dots opacity-[0.08] mix-blend-screen pointer-events-none" />
                    <div aria-hidden className="absolute inset-0 ring-1 ring-inset ring-white/[0.04] rounded-2xl pointer-events-none" />

                    {/* Content — layered inside the card */}
                    <div className="relative z-10 h-full flex flex-col p-6 md:p-7">
                      {/* Top chip — track */}
                      <div className="flex items-start justify-between gap-4">
                        <span className="mono-label text-cyan-glow bg-[#001b36]/70 border border-cyan-glow/30 rounded-full px-3 py-1 text-[10px] backdrop-blur-md">
                          {s.track}
                        </span>
                        <div className="text-right shrink-0">
                          <div className="number-big text-2xl md:text-3xl text-[#ffcb47] whitespace-nowrap text-glow leading-none">{s.score}</div>
                        </div>
                      </div>

                      {/* Spacer pushes name+quote to bottom (cinematic poster feel) */}
                      <div className="flex-1" />

                      <div className="display-serif text-white text-2xl md:text-3xl leading-tight break-words drop-shadow-[0_2px_14px_rgba(0,0,0,0.55)]">
                        {s.name}
                      </div>
                      <div className="mono-label text-[#ffcb47] mt-1.5 mb-3 drop-shadow-[0_1px_6px_rgba(0,0,0,0.55)]">
                        {s.descriptor}
                      </div>
                      {/* Quote — sits on a soft glass slab so it stays legible over any photo */}
                      <div className="rounded-xl bg-ink-900/55 backdrop-blur-md border border-white/[0.06] p-3.5">
                        <p className="text-white/85 font-body text-sm leading-relaxed">{s.quote}</p>
                      </div>
                    </div>
                    <Trophy size={16} className="absolute top-5 right-5 text-[#ffcb47]/50 z-10" />
                  </>
                ) : (
                  <>
                    <div aria-hidden className="absolute top-0 right-0 w-40 h-40 cinematic-dots opacity-10 pointer-events-none" />
                    <div className="relative flex items-start justify-between gap-4 mb-4">
                      <div className="min-w-0 flex-1">
                        <div className="mono-label text-cyan-glow truncate">{s.track}</div>
                        <div className="display-serif text-white text-xl md:text-2xl mt-1 leading-tight break-words">{s.name}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="number-big text-2xl md:text-3xl text-white whitespace-nowrap">{s.score}</div>
                      </div>
                    </div>
                    <div className="mono-label text-[#ffcb47] mb-4">{s.descriptor}</div>
                    <p className="text-white/70 font-body text-sm leading-relaxed">{s.quote}</p>
                    <Trophy size={16} className="absolute bottom-5 right-5 text-white/20 group-hover:text-cyan-glow transition" />
                  </>
                )}
              </motion.article>
            );
          })}
        </div>

        {/* Closing line */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16 text-center"
        >
          <div className="mono-label text-white/40 mb-3">— Still listing, still growing</div>
          <p className="display-italic text-white text-2xl md:text-4xl max-w-4xl mx-auto leading-tight">
            There are many more inspirations. <span className="text-cyan-glow">The next one should be you.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Pricing ---------------- */
const Pricing = ({ onEnroll, courses = [] }) => {
  // Pull the currently-open admin batch to drive the live pricing card.
  // Fall back to static copy only if the admin hasn't set a real batch yet.
  const openCourse = courses.find((c) => c.status === "open") || null;
  const closedCourse = courses.find((c) => c.status === "closed") || null;
  const upcomingCourse = courses.find((c) => c.status === "upcoming") || null;

  const flagshipName = openCourse?.title || "MDCAT Freshers & Repeaters";
  // Prices are fully admin-driven. We deliberately DO NOT show a fake fallback —
  // if the admin hasn't set a price (undefined / null), we render a "Pricing
  // coming soon" state instead of inventing a number. PKR 0 is a VALID price
  // (a freebie / trial cohort) and must render — so we use nullish-coalescing
  // and Number.isFinite instead of `|| null` / `> 0` which used to drop zeros.
  const rawOneTime = openCourse?.price_one_time;
  const rawPerSub  = openCourse?.price_per_subject;
  const flagshipPrice = rawOneTime === undefined || rawOneTime === null ? null : Number(rawOneTime);
  const flagshipPerSubject = rawPerSub === undefined || rawPerSub === null ? null : Number(rawPerSub);
  const hasOneTimePrice = Number.isFinite(flagshipPrice);
  const hasPerSubjectPrice = Number.isFinite(flagshipPerSubject);
  const flagshipOriginal =
    openCourse?.discount_price_one_time && hasOneTimePrice && openCourse.discount_price_one_time > flagshipPrice
      ? openCourse.discount_price_one_time
      : null;
  const flagshipOriginalPerSubject =
    openCourse?.discount_price_per_subject && hasPerSubjectPrice && openCourse.discount_price_per_subject > flagshipPerSubject
      ? openCourse.discount_price_per_subject
      : null;
  const flagshipSubjects = openCourse?.subjects && openCourse.subjects.length
    ? openCourse.subjects
    : ["Biology", "Chemistry", "Physics", "English", "Logical Reasoning"];
  const flagshipBadge = openCourse?.badge || "PMDC BASED";

  // Total batches across statuses — used to decide whether to show an
  // "Explore more batches" link. We count only REAL admin-created batches
  // (those have a `slug`), excluding legacy seeded fillers and the 3 cards
  // already rendered above on this section so the link's number is honest.
  const realAdminBatches = courses.filter((c) => !!c.slug);
  const visibleHere = [openCourse, closedCourse, upcomingCourse].filter(Boolean);
  const visibleHereSlugs = new Set(visibleHere.map((c) => c?.slug).filter(Boolean));
  const additionalBatches = realAdminBatches.filter((c) => !visibleHereSlugs.has(c.slug)).length;
  const showExploreMore = additionalBatches > 0;

  const flagshipRegSlug = openCourse?.registration_slug || openCourse?.slug || null;
  return (
    <section id="pricing" className="relative py-32 md:py-40 border-t border-white/5" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading
          align="center"
          kicker="03 — Investment"
          title={`Pricing · ${flagshipName.split(" ").slice(0, 3).join(" ")}`}
          italic="live from admin."
          subtitle={`Every rupee below is pulled straight from the admin-configured ${flagshipName} batch. Change the fee in the admin panel — it updates here, on batch cards, and in modals instantly.`}
        />

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* MAIN PRICING — Freshers & Repeaters */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative rounded-3xl border border-cyan-glow/40 backdrop-blur-xl overflow-hidden trace-beam bg-gradient-to-br from-ink-700/50 to-ink-850/85"
            data-testid="pricing-tier-freshers-repeaters"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 mono-label bg-cyan-glow text-ink-850 px-4 py-1.5 rounded-full z-10">
              The only active batch · MDCAT 2027
            </span>
            <div aria-hidden className="absolute top-0 right-0 w-80 h-80 cinematic-dots-dense opacity-15 pointer-events-none" />

            <div className="relative p-8 md:p-12">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="mono-label text-cyan-glow mb-3">— The flagship intake</div>
                  <h3 className="display-serif text-white text-4xl md:text-5xl tracking-tight leading-[0.95]">
                    {flagshipName.split(" ").slice(0, 2).join(" ")}
                    <br />
                    <span className="display-italic text-cyan-glow">{flagshipName.split(" ").slice(2).join(" ") || "& Repeaters"}</span>
                  </h3>
                </div>
                <span className="mono-label text-[10px] px-3 py-1.5 rounded-full border border-[#ffcb47]/40 text-[#ffcb47] self-start">
                  {flagshipBadge}
                </span>
              </div>

              {/* The cinematic price reveal */}
              <div className="mt-10 relative">
                {flagshipOriginal && (
                  <div className="flex items-center gap-4 mb-2">
                    <span className="mono-label text-white/45">Original fee</span>
                    <div className="relative">
                      <span className="display-serif text-white/40 text-2xl md:text-3xl tracking-tight">
                        ₨ {Number(flagshipOriginal).toLocaleString()}
                      </span>
                      <span
                        aria-hidden
                        className="absolute left-0 right-0 top-1/2 h-[1.5px] bg-rose-300/70 -rotate-[8deg] origin-center"
                      />
                    </div>
                    <span className="mono-label text-[9px] text-rose-200/70 px-2 py-0.5 rounded-full border border-rose-300/30 bg-rose-300/5">
                      {Math.round(((flagshipOriginal - flagshipPrice) / flagshipOriginal) * 100)}% OFF
                    </span>
                  </div>
                )}

                <div className="flex items-end gap-4 flex-wrap">
                  <div>
                    <div className="mono-label text-cyan-glow mb-1">— your one-time fee</div>
                    {hasOneTimePrice ? (
                      <>
                        <div className="flex items-end gap-2">
                          <span className="mono-label text-cyan-glow text-xl mb-3">₨</span>
                          <span className="number-big text-[6rem] md:text-[8rem] text-white leading-[0.8] text-glow">
                            {Number(flagshipPrice).toLocaleString()}
                          </span>
                        </div>
                        <div className="mono-label text-white/50 mt-2 leading-snug">
                          One-time · all {flagshipSubjects.length} MDCAT subjects · all features
                        </div>
                      </>
                    ) : (
                      <div className="display-italic text-[#ffcb47] text-3xl md:text-4xl tracking-tight leading-none mt-2" data-testid="pricing-coming-soon">
                        Pricing reveal soon
                        <div className="mono-label text-white/45 text-[11px] mt-3 not-italic">
                          The fee for this batch hasn't been finalised yet — check back shortly.
                        </div>
                      </div>
                    )}
                  </div>
                  {hasOneTimePrice && (
                    <div className="ml-auto bg-[#ffcb47]/5 border border-[#ffcb47]/30 rounded-xl px-4 py-3">
                      <div className="mono-label text-[#ffcb47] flex items-center gap-2">
                        <Sparkles size={12} /> For deservings
                      </div>
                      <div className="font-body text-white text-sm mt-1 max-w-[200px] leading-snug">
                        Instalments available — just ask.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-10 horizon" />

              {/* Per-subject alternative */}
              <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-5 items-center">
                <div className="md:col-span-2">
                  <div className="mono-label text-white/45 mb-2">Or pay per subject</div>
                  {hasPerSubjectPrice ? (
                    <>
                      <div className="flex items-end gap-1.5">
                        <span className="mono-label text-white/60 text-sm mb-2">₨</span>
                        <span className="number-big text-5xl md:text-6xl text-white leading-none">{Number(flagshipPerSubject).toLocaleString()}</span>
                        {flagshipOriginalPerSubject && (
                          <span className="ml-2 mb-2 relative">
                            <span className="text-white/35 text-sm">
                              ₨ {Number(flagshipOriginalPerSubject).toLocaleString()}
                            </span>
                            <span aria-hidden className="absolute left-0 right-0 top-1/2 h-[1px] bg-white/30 -rotate-[8deg]" />
                          </span>
                        )}
                      </div>
                      <div className="mono-label text-white/50 mt-2">per subject · all features</div>
                    </>
                  ) : (
                    <div className="text-white/50 font-body text-sm leading-relaxed" data-testid="pricing-per-subject-coming-soon">
                      Per-subject pricing will be announced with the batch launch.
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 flex flex-wrap gap-2">
                  {flagshipSubjects.map((s) => (
                    <span
                      key={s}
                      className="px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs font-body text-white/75"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              {flagshipRegSlug ? (
                <Link
                  to={`/register/${flagshipRegSlug}`}
                  className="btn-primary w-full md:w-auto mt-10 justify-center inline-flex"
                  data-testid="pricing-cta-freshers-repeaters"
                >
                  Open application form <ArrowRight size={18} />
                </Link>
              ) : (
                <button
                  className="btn-primary w-full md:w-auto mt-10 justify-center"
                  onClick={() => onEnroll({ course: flagshipName, plan: `${flagshipName} — One-time` })}
                  data-testid="pricing-cta-freshers-repeaters"
                >
                  Enroll in {flagshipName.split(" ").slice(0, 3).join(" ")} <ArrowRight size={18} />
                </button>
              )}
              <p className="mt-4 mono-label text-white/40">
                Limited bookings · early applicants get priority review
              </p>
            </div>
          </motion.div>

          {/* RIGHT COLUMN — Closed + Upcoming stacked (only shown when live batches exist for those buckets, OR fall back to legacy copy) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Closed — Comeback Program */}
            {(closedCourse || !openCourse) && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative rounded-3xl border border-white/10 bg-ink-850/60 backdrop-blur-xl p-8 md:p-9 overflow-hidden flex-1"
              data-testid="pricing-tier-comeback"
            >
              <div aria-hidden className="absolute inset-0 cinematic-dots opacity-[0.06]" />
              <div className="relative">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <div className="mono-label text-white/50 mb-2">— Intake #02</div>
                    <h3 className="display-serif text-white/85 text-2xl md:text-3xl tracking-tight leading-tight">
                      {closedCourse?.title || "Comeback Program"}
                    </h3>
                  </div>
                  <span className="mono-label text-[10px] px-3 py-1 rounded-full border border-rose-300/40 text-rose-300 bg-rose-300/5 shrink-0">
                    BATCH CLOSED
                  </span>
                </div>
                <div className="display-serif text-white/30 text-3xl md:text-4xl tracking-tight mb-2 relative inline-block">
                  ₨ ——,———
                  <span aria-hidden className="absolute left-0 right-0 top-1/2 h-[1.5px] bg-white/20" />
                </div>
                <p className="text-white/55 font-body text-sm leading-relaxed mt-4 mb-6">
                  {closedCourse?.tagline ||
                    "April's Comeback Batch was our last exclusive Repeaters intake. The good news — Repeaters are equally welcomed inside the currently-open batch. Same features. Same fire."}
                </p>
                <button
                  className="btn-ghost w-full justify-center"
                  onClick={() => onEnroll({ course: flagshipName, plan: `${flagshipName} — One-time`, redirectFromClosed: true })}
                  data-testid="pricing-cta-comeback"
                >
                  Join {flagshipName.split(" ").slice(0, 3).join(" ")} <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>)}

            {/* Upcoming — Parallel Prep */}
            {(upcomingCourse || !openCourse) && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="relative rounded-3xl border border-white/10 bg-ink-850/60 backdrop-blur-xl p-8 md:p-9 overflow-hidden flex-1"
              data-testid="pricing-tier-parallel"
            >
              <div aria-hidden className="absolute top-0 right-0 w-60 h-60 cinematic-dots-dense opacity-10 pointer-events-none" />
              <div className="relative">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <div className="mono-label text-white/50 mb-2">— Intake #03</div>
                    <h3 className="display-serif text-white text-2xl md:text-3xl tracking-tight leading-tight">
                      {upcomingCourse?.title || <>Parallel <span className="display-italic text-cyan-glow">Prep</span></>}
                    </h3>
                  </div>
                  <span className="mono-label text-[10px] px-3 py-1 rounded-full border border-[#ffcb47]/40 text-[#ffcb47] bg-[#ffcb47]/5 animate-pulse-glow shrink-0">
                    REVEAL SOON
                  </span>
                </div>
                <div className="display-italic text-[#ffcb47] text-3xl md:text-4xl tracking-tight leading-none mb-3">
                  Reveal soon
                </div>
                <p className="text-white/55 font-body text-sm leading-relaxed mt-4 mb-6">
                  {upcomingCourse?.tagline ||
                    "For F.Sc Part 1 & Part 2 students preparing MDCAT alongside boards. Launching soon."}
                </p>
                <span
                  className="inline-flex items-center justify-center w-full px-5 py-3 rounded-full border border-white/15 text-white/50 mono-label text-xs cursor-not-allowed"
                  data-testid="pricing-cta-parallel-disabled"
                >
                  Enrollment not yet open
                </span>
              </div>
            </motion.div>)}
          </div>
        </div>

        {/* When the admin has more than 3 batches total, surface a single
            elegant "Explore more batches" link that jumps users to /apply
            where every active cohort is discoverable. */}
        {showExploreMore && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 text-center"
            data-testid="pricing-explore-more"
          >
            <Link
              to="/apply"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-cyan-glow/40 bg-cyan-glow/5 text-cyan-glow font-body text-sm hover:bg-cyan-glow/10 hover:border-cyan-glow/70 transition-all"
            >
              Explore {additionalBatches} more {additionalBatches === 1 ? "batch" : "batches"} <ArrowUpRight size={15} />
            </Link>
            <div className="mt-2 mono-label text-white/40 text-[11px]">
              Full batch directory · all intakes · live from admin
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

/* ---------------- Testimonials ---------------- */
const Testimonials = ({ testimonials }) => {
  const row1 = testimonials.slice(0, 3);
  const row2 = testimonials.slice(3, 6);
  const Card = ({ t }) => (
    <div className="shrink-0 w-[420px] md:w-[540px] rounded-3xl border border-white/10 bg-ink-800/50 backdrop-blur-xl p-8 md:p-10 mr-6">
      <div className="flex items-center gap-1 text-cyan-glow mb-5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={14} fill="currentColor" />
        ))}
      </div>
      <p className="display-italic text-white text-2xl md:text-3xl leading-snug">
        "{t.quote}"
      </p>
      <div className="mt-6 horizon" />
      <div className="mt-6 flex items-center justify-between">
        <div>
          <div className="display-serif text-white text-lg">{t.name}</div>
          <div className="mono-label text-white/50 mt-1">
            {t.city} · <span className="text-cyan-glow">{t.track}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="number-big text-3xl text-[#ffcb47]">{(t.score || "—").split(" ")[0]}</div>
          <div className="mono-label text-white/40">MDCAT</div>
        </div>
      </div>
    </div>
  );

  return (
    <section id="testimonials" className="relative py-32 md:py-40 border-t border-white/5 overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <SectionHeading
          kicker="04 — Voices from the cohort"
          title="Not reviews."
          italic="Evidence."
          subtitle="Three logicians. Three different paths. One standard — premium, relentless, cinematic."
        />
      </div>
      <div className="marquee-wrap mb-6">
        <div className="flex animate-marquee-slow" data-testid="testimonials-row-1">
          {[...row1, ...row1, ...row1].map((t, i) => <Card key={`r1-${i}`} t={t} />)}
        </div>
      </div>
      <div className="marquee-wrap">
        <div className="flex animate-marquee-slow" style={{ animationDirection: "reverse" }} data-testid="testimonials-row-2">
          {[...row2, ...row2, ...row2].map((t, i) => <Card key={`r2-${i}`} t={t} />)}
        </div>
      </div>
    </section>
  );
};

/* ---------------- FAQ ---------------- */
const FAQ = () => {
  const faqs = [
    {
      q: "Who is LogicsWallah primarily for?",
      a: "Pakistani aspirants preparing for MDCAT — whether you're a fresh F.Sc student, a repeater, or pursuing F.Sc and MDCAT simultaneously. Every track is built around your starting point.",
    },
    {
      q: "Is this online, offline, or hybrid?",
      a: "All live cohorts are streamed with cinematic multi-camera production. You can join from anywhere in Pakistan. Recorded content is archived for lifetime access within the cohort.",
    },
    {
      q: "What makes LogicsWallah different from other MDCAT academies?",
      a: "We're not a content library. We're an engineered preparation system — diagnostics, pacing, mentor circles, and exam psychology — built for the top 3%, accessible to the ambitious.",
    },
    {
      q: "Can I switch batches mid-cohort?",
      a: "Yes. But there are certain protocols — it's best to communicate with LW officials directly so your transition is smooth and nothing is lost in translation.",
    },
    {
      q: "Do you offer a guarantee?",
      a: "We guarantee premium quality, engineered pacing, and world-class lectures. We do not guarantee scores — because no serious academy should.",
    },
    {
      q: "How do I enroll?",
      a: "Tap the Enroll button, leave your name, phone, and the plan of your choice. A counselor will call within 12 hours with your personalized roadmap — or simply fill out the registration form for a simpler process.",
    },
  ];
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="relative py-32 md:py-40 border-t border-white/5" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHeading
            kicker="05 — Frequently asked"
            title="Honest answers."
            italic="Zero fluff."
          />
          <p className="mt-8 text-white/60 font-body text-lg leading-relaxed max-w-md">
            Still curious? Our counselors answer the hard questions at 11pm before the test — and all the hours before it.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="space-y-2">
            {faqs.map((f, i) => (
              <div
                key={i}
                className={`rounded-2xl border transition-all ${
                  open === i ? "border-cyan-glow/40 bg-ink-800/60" : "border-white/10 bg-ink-850/40"
                } overflow-hidden`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 p-6 md:p-7 text-left"
                  data-testid={`faq-trigger-${i}`}
                >
                  <div className="flex items-start gap-5">
                    <span className="mono-label text-cyan-glow mt-1 shrink-0">0{i + 1}</span>
                    <span className="display-serif text-white text-xl md:text-2xl tracking-tight leading-snug">
                      {f.q}
                    </span>
                  </div>
                  <motion.span animate={{ rotate: open === i ? 180 : 0 }} className="shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center">
                    <ChevronDown size={16} className={open === i ? "text-cyan-glow" : "text-white/60"} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                    >
                      <div className="px-6 md:px-7 pb-7 pl-[72px] text-white/70 font-body text-base leading-relaxed">
                        {f.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Contact + Footer ---------------- */
const HELPDESK_WHATSAPP = "923148799128"; // +92 314 8799128 (no + or dashes for wa.me)

const ContactAndFooter = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", student_type: "MDCAT Fresher", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    // Build a friendly, complete WhatsApp message from the form fields. We
    // open WhatsApp BEFORE awaiting the API call because most browsers only
    // honour `window.open` inside a user-gesture stack — putting it after an
    // async network round-trip causes pop-up blockers to swallow it.
    const lines = [
      "Hi LogicsWallah! 👋 I'd like to request a counseling call.",
      "",
      `*Name:* ${form.name || "—"}`,
      `*Phone:* ${form.phone || "—"}`,
      `*Email:* ${form.email || "—"}`,
      form.city ? `*City:* ${form.city}` : null,
      `*I am:* ${form.student_type}`,
      form.message ? `*Note:* ${form.message}` : null,
      "",
      "Sent from logicswallah.pk · counseling form",
    ].filter(Boolean);
    const waUrl = `https://wa.me/${HELPDESK_WHATSAPP}?text=${encodeURIComponent(lines.join("\n"))}`;
    // Pop the WhatsApp tab synchronously inside the click handler.
    const waWindow = typeof window !== "undefined" ? window.open(waUrl, "_blank", "noopener") : null;
    try {
      // Save the lead for the admin in parallel — failure here shouldn't block
      // the WhatsApp redirect since the student already has a way to reach us.
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", msg: "Opening WhatsApp… continue the chat there. We've also saved your details." });
      setForm({ name: "", email: "", phone: "", city: "", student_type: "MDCAT Fresher", message: "" });
    } catch (err) {
      // If the form was invalid (e.g. malformed email), surface the error but
      // still leave the WhatsApp tab open — the student can edit their message
      // there and reach a human regardless.
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "We couldn't save your form, but WhatsApp is open — please send it from there.";
      setStatus({ state: "error", msg: typeof msg === "string" ? msg : "Please check your inputs." });
      // If the popup got blocked, expose a fallback link the user can click.
      if (!waWindow && typeof window !== "undefined") {
        window.location.href = waUrl;
      }
    }
  };

  return (
    <section id="contact" className="relative pt-32 pb-0 border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHeading
            kicker="06 — Begin"
            title="Leave your name."
            italic="We'll reshape the rest."
          />
          <div className="mt-12 space-y-5 text-white/70 font-body">
            <a href="tel:+923148799128" className="flex items-center gap-3 hover:text-cyan-glow transition" data-testid="contact-phone">
              <Phone size={16} className="text-cyan-glow" /> +92 314 8799128
            </a>
            <a href="mailto:teamlogicians@gmail.com" className="flex items-center gap-3 hover:text-cyan-glow transition" data-testid="contact-email">
              <Mail size={16} className="text-cyan-glow" /> teamlogicians@gmail.com
            </a>
            <div className="flex items-center gap-3" data-testid="contact-address">
              <MapPin size={16} className="text-cyan-glow" /> Lahore · Karachi · Islamabad · Online
            </div>
            <div className="flex items-center gap-3 text-[#ffcb47]" data-testid="contact-hq">
              <span className="mono-label">HQ</span> · Gujranwala
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="mono-label text-white/50">Full name</label>
                <input required className="cine-input" placeholder="Your name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
              </div>
              <div>
                <label className="mono-label text-white/50">Phone</label>
                <input required className="cine-input" placeholder="+92 ___ ______" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
              </div>
              <div>
                <label className="mono-label text-white/50">Email</label>
                <input required type="email" className="cine-input" placeholder="you@example.com" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
              </div>
              <div>
                <label className="mono-label text-white/50">City</label>
                <input className="cine-input" placeholder="Lahore / Karachi / ..." value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="contact-city-input" />
              </div>
            </div>
            <div>
              <label className="mono-label text-white/50">I am</label>
              <div className="flex flex-wrap gap-2 mt-3">
                {["MDCAT Fresher", "MDCAT Repeater", "Parallel Prep", "Just Exploring"].map((t) => (
                  <button type="button" key={t}
                    onClick={() => setForm({ ...form, student_type: t })}
                    className={`px-4 py-2 rounded-full text-sm font-body border transition ${
                      form.student_type === t
                        ? "border-cyan-glow bg-cyan-glow/10 text-cyan-glow"
                        : "border-white/15 text-white/70 hover:border-white/40"
                    }`}
                    data-testid={`student-type-${t.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                  >{t}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="mono-label text-white/50">Anything specific?</label>
              <textarea rows={3} className="cine-input resize-none" placeholder="Leave us a note (optional)"
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button type="submit" disabled={status.state === "loading"} className="btn-primary" data-testid="contact-submit-btn">
                {status.state === "loading" ? "Opening WhatsApp..." : "Request Counseling Call"} <ArrowRight size={18} />
              </button>
              {status.state === "idle" && (
                <span className="mono-label text-white/40 text-[11px]">Continues on WhatsApp · we'll have your details ready</span>
              )}
              {status.state === "success" && (
                <span className="mono-label text-cyan-glow" data-testid="contact-success">{status.msg}</span>
              )}
              {status.state === "error" && (
                <span className="mono-label text-rose-300" data-testid="contact-error">{status.msg}</span>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-32 relative">
        <div aria-hidden className="absolute inset-0 cinematic-dots-dense opacity-[0.08] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pb-10">
          <div className="horizon mb-12" />
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              <Logo size="lg" />
              <p className="mt-6 text-white/55 font-body max-w-md leading-relaxed">
                Pakistan's most premium &amp; affordable MDCAT academy. Engineered for the top 3%. Accessible to the ambitious.
              </p>
            </div>
            <nav className="flex flex-wrap gap-5 text-white/60 font-body text-sm">
              <a href="#courses" className="hover:text-cyan-glow" data-testid="footer-link-courses">Courses</a>
              <a href="#results" className="hover:text-cyan-glow" data-testid="footer-link-results">Results</a>
              <a href="#pricing" className="hover:text-cyan-glow" data-testid="footer-link-pricing">Pricing</a>
              <a href="#testimonials" className="hover:text-cyan-glow" data-testid="footer-link-testimonials">Voices</a>
              <a href="#faq" className="hover:text-cyan-glow" data-testid="footer-link-faq">FAQ</a>
              <a href="#contact" className="hover:text-cyan-glow" data-testid="footer-link-contact">Contact</a>
            </nav>
          </div>
        </div>
        <div
          aria-hidden
          className="relative overflow-hidden pt-8"
          style={{ height: "clamp(120px, 22vw, 320px)" }}
        >
          <div
            className="absolute inset-x-0 bottom-[-18%] text-center display-serif leading-none text-white/[0.07] select-none whitespace-nowrap"
            style={{ fontSize: "clamp(120px, 28vw, 420px)", letterSpacing: "-0.06em" }}
          >
            LogicsWallah
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pb-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="mono-label text-white/40">© {new Date().getFullYear()} LogicsWallah · All rights reserved</div>
          <div className="mono-label text-white/40">Designed &amp; engineered in Pakistan</div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Enroll Modal ---------------- */
const EnrollModal = ({ open, onClose, preselected }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", plan: "Freshers & Repeaters — One-time", course: "MDCAT Freshers & Repeaters" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  useEffect(() => {
    if (preselected) {
      // If redirecting from closed batch, always set to Freshers & Repeaters
      const courseToSet = preselected.redirectFromClosed 
        ? "MDCAT Freshers & Repeaters" 
        : (preselected.course || preselected.title || "MDCAT Freshers & Repeaters");
      setForm((f) => ({
        ...f,
        plan: preselected.plan || f.plan,
        course: courseToSet,
      }));
    }
  }, [preselected]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/enroll`, {
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        plan: form.plan,
        course: form.course || undefined,
      });
      setStatus({ state: "success", msg: "Enrollment received. Our counselor will call you shortly." });
      setForm({ name: "", phone: "", email: "", plan: "Freshers & Repeaters — One-time", course: "MDCAT Freshers & Repeaters" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Failed. Please try again.";
      setStatus({ state: "error", msg: typeof msg === "string" ? msg : "Check your inputs." });
    }
  };

  const isRepeaterRedirect = preselected?.redirectFromClosed || (preselected?.course && preselected.course.toLowerCase().includes("comeback"));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink-900/80 backdrop-blur-xl"
          onClick={onClose}
          data-testid="enroll-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative w-full max-w-lg rounded-3xl border border-cyan-glow/30 bg-gradient-to-br from-ink-800 to-ink-850 p-8 md:p-10 glow-cyan max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="enroll-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-cyan-glow hover:border-cyan-glow/40 z-10"
              data-testid="enroll-modal-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <div className="mono-label mb-4">— Enroll · MDCAT Batch</div>
            <h3 className="display-serif text-white text-4xl md:text-5xl leading-[0.95] tracking-tight">
              One call. <span className="display-italic text-cyan-glow">One roadmap.</span>
            </h3>
            <p className="mt-4 text-white/60 font-body">
              Leave your details. A counselor will call within 12 hours with your personalized plan.
            </p>

            {isRepeaterRedirect && (
              <div className="mt-5 p-4 rounded-xl border border-[#ffcb47]/30 bg-[#ffcb47]/5 text-[#ffcb47] text-sm">
                Note: The exclusive Repeaters batch has closed. We've pre-selected{" "}
                <strong className="text-white">Freshers &amp; Repeaters</strong> for you — same premium features, same fire.
              </div>
            )}

            <form onSubmit={submit} className="mt-8 space-y-6" data-testid="enroll-form">
              <div>
                <label className="mono-label text-white/50">Full name</label>
                <input required className="cine-input" placeholder="Your name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="enroll-name-input" />
              </div>
              <div>
                <label className="mono-label text-white/50">Phone</label>
                <input required className="cine-input" placeholder="+92 ___ ______" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="enroll-phone-input" />
              </div>
              <div>
                <label className="mono-label text-white/50">Email (optional)</label>
                <input type="email" className="cine-input" placeholder="you@example.com" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enroll-email-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="mono-label text-white/50">Plan</label>
                  <select className="cine-input bg-ink-850" value={form.plan}
                    onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="enroll-plan-select">
                    <option>Freshers &amp; Repeaters — One-time</option>
                    <option>Per-Subject Subscription</option>
                    <option>Instalments (for deservings)</option>
                  </select>
                </div>
                <div>
                  <label className="mono-label text-white/50">Batch</label>
                  <select className="cine-input bg-ink-850" value={form.course}
                    onChange={(e) => setForm({ ...form, course: e.target.value })} data-testid="enroll-course-select">
                    <option>MDCAT Freshers &amp; Repeaters</option>
                    <option disabled>MDCAT Parallel Prep (reveal soon)</option>
                  </select>
                </div>
              </div>
              <button type="submit" disabled={status.state === "loading"}
                className="btn-primary w-full justify-center" data-testid="enroll-submit-btn">
                {status.state === "loading" ? "Sending..." : "Reserve my seat"} <ArrowRight size={18} />
              </button>
              {status.state === "success" && (
                <div className="mono-label text-cyan-glow text-center" data-testid="enroll-success">{status.msg}</div>
              )}
              {status.state === "error" && (
                <div className="mono-label text-rose-300 text-center" data-testid="enroll-error">{status.msg}</div>
              )}
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---------------- App ---------------- */
export default function App() {
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preselect, setPreselect] = useState(null);
  const [batchPickerOpen, setBatchPickerOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/courses`).then((r) => setCourses(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // "Login / Register" → the new StudentAccessModal (login at top, batch apply below).
  // Existing "Pricing → Enroll" still goes direct to the registration form for the open batch.
  const openAccess = () => setAccessOpen(true);

  const openEnroll = (ctx = null) => {
    // If this enrollment context matches an open batch with a real registration slug,
    // send the user to the full registration form instead of the lightweight lead modal.
    const openCourseWithReg = courses.find(c => c.status === "open" && c.registration_slug);
    if (openCourseWithReg && !ctx?.redirectFromClosed && !ctx?.forceLeadModal) {
      window.location.href = `/register/${openCourseWithReg.registration_slug}`;
      return;
    }
    setPreselect(ctx);
    setModalOpen(true);
  };

  const handleBatchPick = (opt) => {
    setBatchPickerOpen(false);
    setTimeout(() => {
      // Only redirect to registration form if THIS batch has its own registration_slug
      // (i.e., the admin has created it). Legacy filler cards (no slug) just scroll
      // to the pricing section so the student still learns about the roadmap.
      if (opt?.registration_slug && (opt.key === "open" || opt.key === "upcoming")) {
        window.location.href = `/register/${opt.registration_slug}`;
        return;
      }
      if (opt.slug) smoothScrollTo(`batch-${opt.slug}`);
      else smoothScrollTo("courses");
    }, 250);
  };

  return (
    <div className="relative bg-ink-850 text-white min-h-screen overflow-x-hidden">
      <CursorFollower />
      <CinematicTexture />
      <BatchesRibbon onOpen={() => { window.location.href = "/apply"; }} />
      <Nav onEnroll={openAccess} onPickBatch={() => { window.location.href = "/apply"; }} />
      <main className="relative z-10">
        <BannerCarousel />
        <Hero onPickBatch={() => { window.location.href = "/apply"; }} />
        <Ticker />
        <Courses
          courses={courses}
          onEnroll={(c) => {
            // Open batch with a real registration slug → go to full registration form.
            if (c && c.registration_slug && c.status === "open" && !c.redirectFromClosed) {
              window.location.href = `/register/${c.registration_slug}`;
              return;
            }
            // Closed batch redirect → lightweight lead capture (keeps existing UX).
            if (c && c.redirectFromClosed) {
              openEnroll(c);
            } else {
              openEnroll({ course: c?.title, plan: "Pro" });
            }
          }}
        />
        <Results />
        <Pricing onEnroll={openEnroll} courses={courses} />
        <Testimonials testimonials={testimonials} />
        <FAQ />
        <ContactAndFooter />
      </main>
      <EnrollModal open={modalOpen} onClose={() => setModalOpen(false)} preselected={preselect} />
      <StudentAccessModal open={accessOpen} onClose={() => setAccessOpen(false)} />
      <BatchSelectorModal
        open={batchPickerOpen}
        onClose={() => setBatchPickerOpen(false)}
        courses={courses}
        onPick={handleBatchPick}
      />
    </div>
  );
}
